// components/AnticipationBDC.tsx
// Interruption d'un bon de caisse validé — même charte que
// ValidationBDC : picklist statut 02 -> Consulter -> champs readonly +
// paramètres d'interruption -> Interrompre (branche 'I' du SP).
import React, { useEffect, useState } from "react";
import { Button, Form, Modal, Space, Spin, Table, Tabs, Tag, Upload } from "antd";
import type { TabsProps } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { UserOutlined, UploadOutlined, DeleteOutlined, FilePdfOutlined, EyeOutlined } from '@ant-design/icons';
import {
  Typography, Box,
} from "@mui/material";
import axios from "axios";
import { IBonDeCaisse, IDemandeInterBDC } from "../../../dataTypes/GlobalDataType";
import ApercuInterruptionBDC, { IApercuInterruption, libModeInter } from "./ApercuInterruptionBDC";
import { Crypter } from "../../../Functions/Security/Crypter";
import Data from '../../../Data/Data.json';
import { useAuthHeader, useAuthUser } from '../../../Services/Auth/sessionAuth';
import Utilisateur from "../../../Models/Utilisateur";
import { fetchDataTable2, fetchJourneeComptable, GlobalExportToCSV, GlobalExportToExcel, GlobalExportToJSON, GlobalExportToPDF, GlobalExportToWord, normalizeKeysToUppercase } from "../../../utilitaires/globalFunction";
import formatNumber from "../../../Functions/Traitement/FormatNumber";
import SweetAlert from "sweetalert2";
import ParamsPicklist from "../../../Types/ParamsPicklist";
import InputForm from "../../InputT";
import SearchInput from "../../SearchInput";
import SearchModal from "../../SearchModal";
import ExportMenu from "../../ExportMenu";

export default function AnticipationBDC() {
  const auth = useAuthUser()
  const authHeader = useAuthHeader()
  const Perss: Utilisateur = JSON.parse(JSON.stringify(auth()))

  const [form] = Form.useForm();
  // Journée comptable lue en direct dans DATGEN (la date de session
  // Perss.datjour peut être périmée)
  const [datJour, setDatJour] = useState<string>(Perss.datjour ? String(Perss.datjour).substring(0, 10) : '');
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<IBonDeCaisse | null>(null);
  // Aperçu calculé par le serveur (paramètres ParamBDC, intérêts, taxes,
  // pénalité, frais) — affiché dans la boîte de dialogue avant la demande
  const [apercu, setApercu] = useState<IApercuInterruption | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  // Boutons (charte)
  const [enregActve, setEnregActve] = useState(true)
  const [echActive, setEchActive] = useState(true)
  const [impActive, setImpActive] = useState(true)
  const [annulActive, setAnnulActive] = useState(true)
  const [consulActive, setConsulActive] = useState(false)
  const [calcActive, setCalcActive] = useState(true)
  const [interActif, setInterActif] = useState(true)   // champs interruption éditables

  interface IEcheance {
    periode: number;
    dateImput: string;
    interet: number;
    taxe: number;
    netAPayer: number;
    statut?: string;   // libellé : 'En attente' | 'Prélevé' | 'Annulée'
    datPrel?: string;  // date de prélèvement (ECPIMPUTPROVBDC.DATPRELEV)
  }
  const [echeancier, setEcheancier] = useState<IEcheance[]>([]);

  // Documents d'interruption — même logique que Safe Care :
  // libellés requis du type (DOCREQINTERBDC) -> picklist -> upload
  // du fichier -> envoyés avec la demande (NUMDEM).
  const [docRequis, setDocRequis] = useState<any[]>([]);
  const [docLibelle, setDocLibelle] = useState<string | null>(null);
  const [pendingDocs, setPendingDocs] = useState<any[]>([]);
  const totalInteret = echeancier.reduce((acc, r) => acc + r.interet, 0);
  const totalTaxe = echeancier.reduce((acc, r) => acc + r.taxe, 0);
  const totalNet = echeancier.reduce((acc, r) => acc + r.netAPayer, 0);

  // Picklist : uniquement les bons VALIDÉS (statut 02 = interruptibles)
  let searchBdc: ParamsPicklist = { CodeAgence: Perss.codage, Table: "BONDECAISSE", Colonne1: "NUMBDC", Colonne2: "NOMCLI", Colagence: 'CODAGE', Choix: 22, colfiltre: "CDETAT", valfiltre: "02" }

  // Motif d'interruption : saisie libre (textarea) par l'utilisateur

  // Libellés des documents requis — picklist sur DOCREQINTERBDC
  // filtrée par le type du bon sélectionné (comme Safe Care)
  let searchDocReq: ParamsPicklist = { CodeAgence: Perss.codage, Table: "DOCREQINTERBDC", Colonne1: "IDREQ", Colonne2: "LIBDOC", Colagence: 'CODAGE', Choix: 22, colfiltre: "CODBDC", valfiltre: selected?.TYPBDC || '___' }

  function initialiazeButton() {
    setEnregActve(true)
    setEchActive(true)
    setImpActive(true)
    setAnnulActive(true)
    setConsulActive(false)
    setCalcActive(true)
    setInterActif(true)
  }

  function handleAnulle() {
    setEcheancier([])
    setSelected(null)
    setApercu(null)
    setPreviewOpen(false)
    setDocRequis([])
    setDocLibelle(null)
    setPendingDocs([])
    form.resetFields()
    initialiazeButton()
  }

  function cunsultButton() {
    setEnregActve(false)   // Interrompre disponible
    setEchActive(false)
    setImpActive(false)
    setAnnulActive(false)
    setConsulActive(false)
    setCalcActive(false)   // Calculer disponible
    setInterActif(false)   // champs interruption éditables
  }

  // Liste des bons validés (état 02)
  const fetchData = async (): Promise<IBonDeCaisse[]> => {
    try {
      var params = { CodeAgence: Perss.codage, Table: "BONDECAISSE", Colonne1: "NUMCLI", Colonne2: "NOMCLI", Colagence: "CODAGE", Init: "", choix: 32, colfiltre: "", valfiltre: "" }
      const res = await fetchDataTable2(Data.ApiUrl.AnySearch, params, authHeader());
      const list: IBonDeCaisse[] = (res || []).filter((c: IBonDeCaisse) => c.CDETAT === "02")
      return list
    } catch (err) {
      console.error("Erreur de chargement:", err);
      return [];
    }
  };

  // Libellé du code opération du bon (OPERAT.LIBELL)
  const fetchLibOpe = async (codope?: string) => {
    if (!codope) { form.setFieldValue('LIBOPE', ''); return; }
    try {
      var params = { CodeAgence: '', Table: "OPERAT", Colonne1: "CODOPE", Colonne2: "LIBELL", Colagence: '', Init: "", choix: 32, colfiltre: "", valfiltre: "" }
      const res = await fetchDataTable2(Data.ApiUrl.AnySearch, params, authHeader());
      const row = (res || []).find((o: any) => o.CODOPE === codope);
      form.setFieldValue('LIBOPE', row?.LIBELL || '');
    } catch (err) {
      console.warn('fetchLibOpe - erreur', err);
    }
  };

  // Aperçu de l'interruption calculé PAR LE SERVEUR (fn_BDC_CalculInterruption) :
  // paramètres du type (onglet « Paramètre interruption » de ParamBDC),
  // intérêts, taxes dues / récupérées, pénalité, frais, net client +
  // contrôles bloquants / avertissements. Aucun montant n'est calculé ici.
  const fetchApercu = async (numBdc?: string, datInterup?: string): Promise<IApercuInterruption | null> => {
    if (!numBdc) return null;
    try {
      const payload: IDemandeInterBDC = { NUMBDC: numBdc, CODAGE: Perss.codage, DATINTERUP: datInterup };
      const response = await axios({
        method: 'get',
        maxBodyLength: Infinity,
        url: Data.ApiUrl.ApercuInterruptionBDC,
        headers: {
          'CypherDemandeInter': Crypter.encrypt(JSON.stringify(payload)),
          'Authorization': authHeader(),
          'Content-Type': 'application/json'
        }
      });
      if (!response.data) return null;
      const res = JSON.parse(Crypter.decrypt(response.data));
      if (res.StatutCode == 200 && res.Result) return res.Result as IApercuInterruption;
      SweetAlert.fire({ icon: 'warning', title: 'Aperçu interruption', text: res.Message || 'Calcul indisponible' });
      return null;
    } catch (err) {
      console.error('ApercuInterruption - erreur', err);
      SweetAlert.fire({ icon: 'error', title: 'Aperçu interruption', text: String(err) });
      return null;
    }
  };

  // Disponible d'un compte (GetCompte → COMPTES, même endpoint que création)
  const fetchDispon = async (numcpt?: string, field?: string) => {
    if (!numcpt || !field) return;
    try {
      const crypted = Crypter.encrypt(String(numcpt));
      const response = await axios({
        method: 'get',
        maxBodyLength: Infinity,
        url: Data.ApiUrl.GetCompte,
        headers: {
          'CypherNumCpte': crypted,
          'Authorization': authHeader(),
          'Content-Type': 'application/json'
        }
      });
      if (response.data) {
        const res = JSON.parse(Crypter.decrypt(response.data));
        if (res.StatutCode == 200 && res.Result?.Table?.[0] != null) {
          form.setFieldValue(field, formatNumber(Number(res.Result.Table[0].DISPON) || 0));
        }
      }
    } catch (err) {
      console.warn('GetCompte - erreur', err);
    }
  };

  const fmtDate = (d: any) => d ? new Date(d).toISOString().substring(0, 10) : "";

  // Nombre de jours par fréquence
  const joursFreq = (freq: string) => {
    switch ((freq || "").toUpperCase()) {
      case "A": return 360;
      case "S": return 180;
      case "T": return 90;
      case "M": return 30;
      case "B": return 15;
      case "F": return 60;
      case "H": return 7;
      case "J": return 1;
      default: return 30;
    }
  }

  // Échéancier (aperçu — même calcul front que création)
  const calculer = (row: any) => {
    const freq = (row.FREQPROV || "").toUpperCase();
    const montant = Number(row.MONTANT) || 0;
    const tauxInteret = Number(row.TXINT) || 0;
    const tauxTaxe = Number(row.TXTAXE) || 0;
    const duree = Number(row.DUREE) || 0;

    const jours = joursFreq(freq);
    const montIntBrut = (montant * tauxInteret * duree * jours) / 36000;
    const montProvFreq = Math.round(montIntBrut / duree);
    const montInt = montProvFreq * duree;
    const montTaxeBrut = (montInt * tauxTaxe) / 100;
    const montTaxeFreq = Math.round(montTaxeBrut / duree);
    const montTaxe = montTaxeFreq * duree;

    let dateEcheance = new Date(row.DATDEBUT || Perss.datjour);
    const newEcheancier: IEcheance[] = [];
    for (let i = 1; i <= duree; i++) {
      const prochaine = new Date(dateEcheance);
      prochaine.setDate(prochaine.getDate() + jours);
      newEcheancier.push({
        periode: i,
        dateImput: dateEcheance.toLocaleDateString(),
        interet: montProvFreq,
        taxe: montTaxeFreq,
        netAPayer: montProvFreq - montTaxeFreq,
        statut: 'En attente',   // écrasé par les lignes ECPIMPUTPROVBDC réelles si lues
      });
      dateEcheance = prochaine;
    }

    setEcheancier(newEcheancier);
    form.setFieldsValue({
      TotInt: formatNumber(montInt),
      TotTaxe: formatNumber(montTaxe),
      TotNet: formatNumber(montInt - montTaxe),
      MTNINT: montInt,
      MTNTAXE: montTaxe,
      MTNNETAPAYER: montInt - montTaxe,
      MTNPROVFREQ: montProvFreq,
      MTNTAXEFREQ: montTaxeFreq,
    });
    setEchActive(false);
  }

  // Libellés des documents requis pour le type (DOCREQINTERBDC)
  const fetchDocRequis = async (typBdc?: string) => {
    if (!typBdc) { setDocRequis([]); return; }
    try {
      var params = { CodeAgence: Perss.codage, Table: "DOCREQINTERBDC", Colonne1: "IDREQ", Colonne2: "LIBDOC", Colagence: "CODAGE", Init: "", choix: 32, colfiltre: "", valfiltre: "" }
      const res = await fetchDataTable2(Data.ApiUrl.AnySearch, params, authHeader());
      setDocRequis((res || []).filter((d: any) => d.CODBDC === typBdc));
    } catch (err) {
      console.error("Erreur de chargement:", err);
    }
  };

  // Upload d'un fichier pour le libellé sélectionné — conservé en
  // mémoire (pendingDocs) jusqu'à la création de la demande
  const handleUploadDoc = (file: File) => {
    if (!docLibelle) {
      SweetAlert.fire({ icon: 'warning', title: 'Document', text: 'Sélectionnez d\'abord le libellé du document dans la picklist.' });
      return false;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setPendingDocs((prev) => [...prev, {
        LIBDOC: docLibelle,
        IDREQ: Number(form.getFieldValue('DOCREQ')) || null,
        NOMFICHIER: file.name,
        PIECEJOINTE: reader.result,
      }]);
    };
    reader.readAsDataURL(file);
    return false;   // empêche l'upload auto d'antd
  };

  // Upload des documents rattachés à la demande créée (NUMDEM)
  // Retourne la liste des documents dont l'enregistrement a échoué
  const uploadDocsInterruption = async (numDem: string, numBdc: string): Promise<string[]> => {
    const echecs: string[] = [];
    for (const doc of pendingDocs) {
      try {
        const payload = { ...doc, NUMDEM: numDem, NUMBDC: numBdc, CODAGE: Perss.codage, UTISAI: Perss.coduti };
        const data = Crypter.encrypt(JSON.stringify(payload));
        const response = await axios.request({
          method: 'post',
          maxBodyLength: Infinity,
          url: Data.ApiUrl.AddDocInterruptionBDC,
          headers: {
            'Authorization': authHeader(),
            'Content-Type': 'application/json'
          },
          data: JSON.stringify(data)   // payload chiffré dans le BODY (trop gros pour un header)
        });
        const res = response.data ? JSON.parse(Crypter.decrypt(response.data)) : null;
        if (res?.StatutCode != 200) echecs.push(`${doc.LIBDOC} (${res?.Message || 'réponse vide'})`);
      } catch (err) {
        console.error('Erreur upload document:', err);
        echecs.push(`${doc.LIBDOC} (${String(err)})`);
      }
    }
    return echecs;
  };

  // Calcul de l'interruption : délégué au serveur (même calcul que la
  // validation et que la comptabilisation). Remplit les champs affichés.
  async function calculInterruptionBDC(numBdc?: string, datInterup?: string): Promise<IApercuInterruption | null> {
    const a = await fetchApercu(numBdc || form.getFieldValue('NUMBDC'), datInterup || form.getFieldValue('DATINTERUP'));
    setApercu(a);
    if (!a) return null;
    form.setFieldsValue({
      JOURRESTANT: Number(a.JOURRESTANT) || 0,
      JOURECOULE: Number(a.JOURECOULE) || 0,
      PENALITE: formatNumber(Number(a.PENALITE) || 0),
      INTACQUIS: Number(a.INTACQUIS) || 0,
      INTDUS: Number(a.INTDUS) || 0,
      MODEINTER: a.MODEINT || 'NON',
      INTEFFET: libModeInter(a.MODEINT),
    });
    return a;
  }

  // Échéancier réel (ECPIMPUTPROVBDC) : statut de dotation SOD par ligne
  // ('02' = prélevée + DATPRELEV, '03' = annulée par interruption).
  // Le bon affiché ici est validé -> les lignes existent toujours ;
  // en cas d'échec de lecture on garde l'échéancier recalculé.
  const fetchEcheancierReel = async (numbdc?: string) => {
    if (!numbdc) return;
    try {
      var params = { CodeAgence: Perss.codage, Table: "ECPIMPUTPROVBDC", Colonne1: "NUMBDC", Colonne2: "DATIMPUT", Colagence: "CODAGE", Init: "", choix: 32, colfiltre: "", valfiltre: "" }
      const res = await fetchDataTable2(Data.ApiUrl.AnySearch, params, authHeader());
      const rows = (normalizeKeysToUppercase(res) || [])
        .filter((e: any) => e.NUMBDC === numbdc)
        .sort((a: any, b: any) => (Number(a.NUMSEQ) || 0) - (Number(b.NUMSEQ) || 0));
      if (!rows.length) return;
      setEcheancier(rows.map((e: any, i: number) => ({
        periode: i + 1,
        dateImput: e.DATIMPUT ? new Date(e.DATIMPUT).toLocaleDateString() : '',
        interet: Number(e.MTNINT) || 0,
        taxe: Number(e.MTNTAXE) || 0,
        netAPayer: (Number(e.MTNINT) || 0) - (Number(e.MTNTAXE) || 0),
        statut: e.CDETAT === '02' ? 'Prélevé' : e.CDETAT === '03' ? 'Annulée' : 'En attente',
        datPrel: e.DATPRELEV ? new Date(e.DATPRELEV).toLocaleDateString() : '',
      })));
    } catch (err) { console.warn('fetchEcheancierReel - erreur', err); }
  };

  // Colonnes antd de l'échéancier (même table que création/validation)
  const columns: ColumnsType<IEcheance> = [
    { title: 'Période', dataIndex: 'periode', key: 'periode', width: 80, align: 'center' },
    { title: 'Date imputation', dataIndex: 'dateImput', key: 'dateImput', width: 120 },
    { title: 'Intérêt', dataIndex: 'interet', key: 'interet', width: 130, align: 'right', render: (v: number) => formatNumber(v) },
    { title: 'Taxe', dataIndex: 'taxe', key: 'taxe', width: 110, align: 'right', render: (v: number) => formatNumber(v) },
    { title: 'Net à payer', dataIndex: 'netAPayer', key: 'netAPayer', width: 130, align: 'right', render: (v: number) => formatNumber(v) },
    {
      title: 'Statut', dataIndex: 'statut', key: 'statut', width: 150, align: 'center',
      render: (_: any, r: IEcheance) => (
        <Tag color={r.statut === 'Prélevé' ? 'green' : r.statut === 'Annulée' ? 'red' : 'orange'}>
          {r.statut || 'En attente'}{r.statut === 'Prélevé' && r.datPrel ? ` le ${r.datPrel}` : ''}
        </Tag>
      ),
    },
  ];

  const tabElement = (
    <Box fontStyle="inherit" className='tabElement' style={{ top: '95%', height: 'auto' }}>
      <Spin spinning={loading}>
        <Table size='small' style={{ width: 'auto' }} columns={columns} dataSource={echeancier} rowKey="periode" scroll={{ y: 160, x: 10 }} pagination={false} />
      </Spin>
      <Box display={"flex"} fontStyle="inherit" className='border-left bordure1' style={{ marginTop: 8 }}>
        <InputForm notRequired disabled width={8} label='Intérêts bruts' name='TotInt' type='text' />
        <InputForm notRequired disabled width={8} label='Taxe retenue' name='TotTaxe' type='text' />
        <InputForm notRequired disabled width={8} label='Net versé au client' name='TotNet' type='text' />
      </Box>
    </Box>
  );

  // Exports du tableau
  const handleExport = (format: any) => {
    const title = "Exportation interruption BDC " + (selected?.NUMBDC || '');
    const exportData = echeancier.length > 0 ? echeancier : (selected ? [selected] : []);
    switch (format) {
      case 'excel': GlobalExportToExcel(exportData, title, "InterruptionBDC"); break;
      case 'pdf': GlobalExportToPDF(exportData, title, "InterruptionBDC"); break;
      case 'word': GlobalExportToWord(exportData, title, "InterruptionBDC"); break;
      case 'csv': GlobalExportToCSV(exportData, title, "InterruptionBDC"); break;
      case 'json': GlobalExportToJSON(exportData, title, "InterruptionBDC"); break;
      default: break;
    }
  };

  // Remplit le formulaire depuis un bon validé
  const fillFromBdc = (row: IBonDeCaisse) => {
    const cleanRow: any = {
      ...row,
      DATDEBUT: fmtDate(row.DATDEBUT),
      DATFIN: fmtDate(row.DATFIN),
      DATINTERUP: fmtDate(datJour),
      JOURRESTANT: 0,
      JOURECOULE: 0,
      PENALITE: 0,
      INTACQUIS: 0,
      INTDUS: 0,
      MODEINTER: '',
      INTEFFET: '',
      lib_NUMCLI: row.NOMCLI,
    }
    setSelected(row);
    form.setFieldsValue(cleanRow);
    fetchDispon(row.CPTDEPOT, 'DISPONCPTDEPOT')
    fetchDispon(row.CPTBDC, 'DISPONCPTBDC')
    fetchDispon(row.CPTINTCLI, 'DISPONCPTINT')
    fetchLibOpe(row.CODOPE)
    fetchDocRequis(row.TYPBDC)
    calculer({ ...row })              // aperçu immédiat, remplacé par les lignes réelles
    fetchEcheancierReel(row.NUMBDC)
    calculInterruptionBDC(row.NUMBDC, fmtDate(datJour))
  }

  // Consulter : charge le bon validé dont le numéro est saisi
  const consult = async () => {
    const numbdc = form.getFieldValue("NUMBDC")
    if (!numbdc) {
      SweetAlert.fire({ title: "Sélectionnez ou saisissez d'abord un numéro de bon de caisse validé.", icon: "warning" })
      return
    }
    setLoading(true)
    const list = await fetchData()
    setLoading(false)
    const row = list.find(c => c.NUMBDC === numbdc)
    if (!row) {
      SweetAlert.fire({ title: "Bon de caisse introuvable ou non validé", text: "Aucun bon validé (statut 02) pour le numéro : " + numbdc, icon: "warning" })
      return
    }
    fillFromBdc(row)
    cunsultButton()
  }

  useEffect(() => {
    form.setFieldValue('DATINTERUP', fmtDate(datJour))
    // Journée comptable en direct (DATGEN) — pas la date de session
    fetchJourneeComptable(Perss.codage)
      .then((jour) => {
        if (jour) {
          setDatJour(jour);
          form.setFieldValue('DATINTERUP', jour);
        }
      });
  }, []);

  // Recalcule (serveur) au changement de la date d'interruption
  const onValuesChange = (changed: any) => {
    // Picklist document : le libellé choisi conditionne l'upload
    if (changed.lib_DOCREQ !== undefined || changed.DOCREQ !== undefined) {
      setDocLibelle(form.getFieldValue('lib_DOCREQ') || null);
    }
    if (!selected) return;
    if (changed.DATINTERUP !== undefined && !interActif) {
      calculInterruptionBDC(selected.NUMBDC, changed.DATINTERUP);
    }
  };

  // ------------------------------------------------------------------
  // « Demander » : contrôles de saisie -> aperçu serveur dans la boîte
  // de dialogue (paramètres ParamBDC, taxes dues / récupérées, pénalité,
  // frais, net client). La demande n'est créée qu'à la confirmation.
  // ------------------------------------------------------------------
  const onFinish = async () => {
    const values = form.getFieldsValue(true);

    if (!selected || selected.CDETAT !== '02') {
      SweetAlert.fire({
        icon: "warning",
        title: "Interruption bon de caisse",
        text: "Impossible d'interrompre ce bon de caisse : il n'est pas validé ou a déjà été traité.",
        target: document.body,
        backdrop: true,
      });
      return;
    }

    if (!values.MOTIF || !String(values.MOTIF).trim()) {
      SweetAlert.fire({ icon: "warning", title: "Motif obligatoire", text: "Saisissez le motif de l'interruption.", target: document.body, backdrop: true });
      return;
    }

    // Documents obligatoires configurés pour le type : tout libellé
    // marqué 'O' doit avoir son fichier uploadé (re-contrôlé par le
    // serveur à la validation)
    const manquants = docRequis.filter((d: any) => d.OBLIGATOIRE === 'O' && !pendingDocs.some((p) => p.IDREQ === d.IDREQ));
    if (manquants.length > 0) {
      SweetAlert.fire({
        icon: "warning",
        title: "Documents obligatoires manquants",
        text: `Uploadez les documents : ${manquants.map((m: any) => m.LIBDOC).join(', ')}.`,
        target: document.body,
        backdrop: true,
      });
      return;
    }

    setLoading(true);
    const a = await calculInterruptionBDC(selected.NUMBDC, values.DATINTERUP);
    setLoading(false);
    if (a) setPreviewOpen(true);
  };

  // Confirmation dans la boîte de dialogue -> création de la demande.
  // Seuls les éléments saisis sont transmis : le serveur recalcule et
  // enregistre lui-même tous les montants.
  const confirmerDemande = () => {
    const values = form.getFieldsValue(true);
    const demande: IDemandeInterBDC = {
      NUMBDC: values.NUMBDC,
      DATINTERUP: values.DATINTERUP,
      MOTIF: values.MOTIF,
      LIBMOTIF: values.lib_MOTIF || values.MOTIF,
      OBSERVATION: values.OBSERVATION,
      UTISAI: Perss.coduti,
      CODAGE: Perss.codage,
    }

    setLoading(true);
    let data = Crypter.encrypt(JSON.stringify(demande));
    let config = {
      method: 'post',
      maxBodyLength: Infinity,
      url: Data.ApiUrl.DemanderInterruptionBDC,
      headers: {
        'CypherDemandeInter': data,
        'Authorization': authHeader(),
        'Content-Type': 'application/json'
      },
      data: data
    };

    axios.request(config)
      .then(async (response) => {
        if (!response.data) { setLoading(false); return; }
        const res = JSON.parse(Crypter.decrypt(response.data));
        if (res.StatutCode == 200) {
          const numDem = String(res.Result);   // NUMDEM généré côté serveur
          const nbDocs = pendingDocs.length;
          // Upload des documents rattachés à la demande (Safe Care)
          const echecs = nbDocs > 0 ? await uploadDocsInterruption(numDem, demande.NUMBDC || '') : [];
          setLoading(false);
          handleAnulle();
          SweetAlert.fire(echecs.length > 0 ? {
            title: "Demande enregistrée — documents incomplets",
            text: `${res.Message} Échec d'enregistrement de : ${echecs.join(' ; ')}. La validation sera refusée tant que les documents obligatoires manquent.`,
            icon: "warning"
          } : {
            title: "Demande enregistrée",
            text: `${res.Message}${nbDocs > 0 ? ` (${nbDocs} document(s) joint(s))` : ''} Elle sera effective après validation par un autre utilisateur.`,
            icon: "success"
          })
        }
        else {
          setLoading(false);
          SweetAlert.fire({ title: "Warning", text: res.Message, icon: "warning" })
        }
      })
      .catch((error) => {
        setLoading(false);
        console.log(error)
        SweetAlert.fire({ title: "Error Front", text: String(error), icon: "error" })
      });
  };

  const onFinishFailed = async (errorInfo: any) => {
    var errors: any = [];
    errorInfo.errorFields.map((error: any) => {
      errors.push(error.name + "");
    })
    SweetAlert.fire({ title: "Veuillez remplir tous les champs obligatoires", text: errors.toString().replaceAll(',', " - "), icon: 'warning', confirmButtonText: 'OK' })
  };

  // ------------------------------------------------------------------
  // Onglet interruption (même layout que validation)
  // ------------------------------------------------------------------
  const items: TabsProps['items'] = [
    {
      key: '1',
      label: (
        <span id='Tab'>
          <UserOutlined />
          Interruption bon de caisse
        </span>
      ),
      children: (
        <div className='tabItem'>
          <div>
            <Box fontStyle="inherit" className='border' width={"85%"} position={"absolute"}>
              <Box display={"flex"} fontStyle="inherit" className='border-left'>

                <Box fontStyle="inherit" className='border-left' style={{ flex: 1 }}>
                  <SearchInput dissabledCode={!consulActive && !annulActive} notRequired notremove enable width={7} form={form} Titre='Recherche Bon de caisse' label='N° BDC' name='NUMBDC' Parametre={searchBdc} />
                  <InputForm width={11} label='Référence BDC' name='REFBDC' type='label' notRequired />
                  <InputForm width={11} label='Code Simul' name='CODSIMUL' type='label' notRequired />
                  <InputForm width={11} label='Indice client' name='NUMCLI' type='label' notRequired />
                  <InputForm width={11} label='Nom client' name='lib_NUMCLI' type='label' notRequired />
                  <InputForm width={11} label='Pénalité interruption' name='PENALITE' type='label' notRequired />
                  <InputForm width={11} label='Date interruption' name='DATINTERUP' type='date' notRequired disabled={interActif} />
                  <InputForm width={11} label='Intérêts acquis' name='INTACQUIS' type='label' notRequired />
                  <InputForm width={11} label='Intérêts dus' name='INTDUS' type='label' notRequired />
                </Box>

                <Box fontStyle="inherit" className='border-left' style={{ flex: 1 }}>
                  <InputForm width={11} label='Type BDC' name='TYPBDC' type='label' notRequired />
                  <InputForm width={11} label='Type imputation' name='TYPIMPUTINT' type='label' notRequired />
                  <InputForm width={11} label='Fréquence' name='FREQPROV' type='label' notRequired />
                  <InputForm width={11} label='Durée' name='DUREE' type='label' notRequired />
                  <InputForm width={11} label='État' name='CDETAT' type='label' notRequired />
                  <InputForm width={11} label='Jours écoulés' name='JOURECOULE' type='label' notRequired />
                  <InputForm width={11} label='Mode sortie' name='MODEINTER' type='label' notRequired />
                  <InputForm width={11} label='Effet intérêts' name='INTEFFET' type='label' notRequired />
                </Box>

                <Box fontStyle="inherit" className='border-left' style={{ flex: 1 }}>
                  <InputForm width={11} label='Montant' name='MONTANT' type='label' notRequired />
                  <InputForm width={11} label='Date début' name='DATDEBUT' type='label' notRequired />
                  <InputForm width={11} label='Date fin' name='DATFIN' type='label' notRequired />
                  <InputForm width={11} label='Taux intérêt' name='TXINT' type='label' notRequired />
                  <InputForm width={11} label='Taux taxe' name='TXTAXE' type='label' notRequired />
                  <InputForm width={11} label='Jours restants' name='JOURRESTANT' type='label' notRequired />
                  <InputForm width={11} label='Motif' name='MOTIF' type='TextArea' row={3} placeholder="Saisir le motif de l'interruption" notRequired />
                  <InputForm width={11} label='Observation' name='OBSERVATION' type='text' notRequired />
                </Box>

                <Box fontStyle="inherit" className='border-left' style={{ flex: 1 }}>
                  <InputForm width={11} label='Compte dépôt' name='CPTDEPOT' type='label' notRequired />
                  <InputForm width={11} label='Disponible' name='DISPONCPTDEPOT' type='label' notRequired />
                  <InputForm width={11} label='Compte BDC' name='CPTBDC' type='label' notRequired />
                  <InputForm width={11} label='Disponible' name='DISPONCPTBDC' type='label' notRequired />
                  <InputForm width={11} label='Compte intérêts' name='CPTINTCLI' type='label' notRequired />
                  <InputForm width={11} label='Disponible' name='DISPONCPTINT' type='label' notRequired />
                  <SearchModal notremove notRequired disabled={enregActve} width={11} Taille1="35%" Taille2="65%" form={form} Titre='Recherche document' label='Document' name='DOCREQ' Parametre={searchDocReq}
                    ExecuteafterSelect1={() => setDocLibelle(String(form.getFieldValue('lib_DOCREQ') || '') || null)} />
                  <Upload beforeUpload={handleUploadDoc} showUploadList={false} accept=".pdf,.png,.jpg,.jpeg">
                    <Button size='small' icon={<UploadOutlined />} disabled={enregActve || !docLibelle} block style={{ marginTop: 4 }}>
                      Uploader le document
                    </Button>
                  </Upload>
                  {/* Tags de statut + fichiers uploadés — clic pour
                      prévisualiser */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
                    {docRequis.map((d: any) => {
                      const fourni = pendingDocs.some((p) => p.IDREQ === d.IDREQ);
                      return (
                        <span key={d.IDREQ} style={{
                          fontSize: 11, padding: '2px 8px', borderRadius: 10,
                          border: `1px solid ${fourni ? '#116599' : '#d4380d'}`,
                          background: fourni ? '#e8f4fb' : '#fff1f0',
                          color: fourni ? '#116599' : '#d4380d',
                        }}>
                          {d.OBLIGATOIRE === 'O' ? '* ' : ''}{d.LIBDOC}{fourni ? ' ✓' : ' ✗'}
                        </span>
                      );
                    })}
                  </div>
                  {pendingDocs.map((d, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, marginTop: 4 }}>
                      <FilePdfOutlined style={{ color: '#b00' }} />
                      <span
                        style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer', color: '#116599', textDecoration: 'underline' }}
                        title={d.NOMFICHIER}
                        onClick={() => {
                          const win = window.open();
                          win?.document.write(`<iframe src="${d.PIECEJOINTE}" style="width:100%;height:100vh;border:0"></iframe>`);
                        }}
                      >
                        <b>{d.LIBDOC}</b> — {d.NOMFICHIER}
                      </span>
                      <DeleteOutlined style={{ color: 'darkred', cursor: 'pointer' }} onClick={() => setPendingDocs(pendingDocs.filter((_, j) => j !== i))} />
                    </div>
                  ))}
                </Box>

              </Box>

            </Box>
          </div>
        </div>
      ),
    }
  ];

  return (
    <div className='tab'>
      <Typography align='center' className='formTitle'> <b> INTERRUPTION BON DE CAISSE </b> </Typography>
      <Spin spinning={loading} size='large' style={{ color: '#116599' }}>
        <Form autoComplete="off" labelAlign='left' form={form} onFinish={onFinish} onFinishFailed={onFinishFailed} onValuesChange={onValuesChange} style={{ minWidth: 'auto', maxWidth: 'auto' }}>
          <div className='bG'>
            <legend><Typography color={"#116599"}>Actions</Typography></legend>
            <Form.Item shouldUpdate className='bouttong'>
              <Space direction="vertical" style={{ width: '100%' }}>
                <Button size='large' className='bt_add' onClick={() => { consult(); }} disabled={consulActive} type="ghost" htmlType="button" block  >
                  Consulter
                </Button>
                <Button size='large' className='bt_add' onClick={() => { calculer(selected || {}); calculInterruptionBDC(); }} disabled={calcActive} type="ghost" htmlType="button" block  >
                  Calculer
                </Button>
                <ExportMenu disabled={impActive} onExport={handleExport} />
                <Button size='large' className='bt_add' disabled={enregActve} type="ghost" htmlType="submit" block>
                  Demander
                </Button>
                <Button size='large' onClick={handleAnulle} disabled={annulActive} className='bt_add' type="ghost" htmlType="reset" block>
                  Annuler
                </Button>
              </Space>
            </Form.Item>
          </div>
          <div className='clientP'>
            <div>
              <div >
                <Tabs size='small' type="card" defaultActiveKey="1" items={items} />
                {tabElement}
              </div>
            </div>
            <div>
            </div>
          </div>
        </Form>
      </Spin>

      {/* Aperçu de la demande avant création — même gabarit que la
          boîte « Aperçu — validation interruption » */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 15, fontWeight: 600, color: 'var(--darkblue)' }}>
            <EyeOutlined style={{ color: 'var(--darkColor)' }} />
            Aperçu — demande d'interruption {selected?.NUMBDC}
          </div>
        }
        open={previewOpen}
        onCancel={() => setPreviewOpen(false)}
        width="90%"
        centered
        zIndex={2050}
        style={{ maxWidth: '1400px' }}
        footer={[
          <Button key="cancel" onClick={() => setPreviewOpen(false)}>Annuler</Button>,
          <Button key="ok" type="primary" onClick={() => { setPreviewOpen(false); confirmerDemande(); }} loading={loading}
            disabled={!apercu || (apercu.BLOQUANTS || []).length > 0} style={{ background: '#116599' }}>
            Confirmer la demande
          </Button>
        ]}
        bodyStyle={{ maxHeight: 'calc(100vh - 160px)', overflow: 'auto', padding: 16 }}
      >
        {apercu && (
          <ApercuInterruptionBDC apercu={apercu} entete={[
            ['Motif', form.getFieldValue('lib_MOTIF') || form.getFieldValue('MOTIF')],
            ['Observation', form.getFieldValue('OBSERVATION') || '—'],
            ['Demandeur', Perss.coduti],
            ['Documents joints', `${pendingDocs.length} / ${docRequis.length} requis`],
          ]} />
        )}
      </Modal>
    </div>
  );
}
